import { bunja } from 'bunja';
import { atom } from 'jotai';
import { Actor, Subscription, createActor } from 'xstate';

import { MfaScope, MfaVerifyChannel } from '@flipster/apis/v1/mfa/types';
import { authBunja } from '@flipster/features/src/shared/auth/state';
import { overlayBunja } from '@flipster/overlay';
import { snackbar } from '@flipster/react/design-system/snackbar';
import { JotaiStoreScope } from '@flipster/utils/bunja-jotai';
import { getErrorCode, getErrorMessage } from '@flipster/utils/error';

import MfaVerifyFlowModal from './MfaVerifyFlowModal';
import { emailOtpBunja } from './_form/EmailOtpInput';
import { smsOtpBunja } from './_form/SmsOtpInput';
import { totpOtpBunja } from './_form/TotpOtpInput';
import mfaVerifyMachine from './machine';

const passkeyUnavailableScope: MfaScope[] = ['RESET_PASSWORD'];

const MfaVerifyFlowModalId = 'MfaVerifyFlowModal';

/**
 * OTP 입력 폼 상태의 수명은 모달(MfaForm)과 함께 끝난다.
 * flow bunja 가 이 bunja 를 참조하면(flow -> form) 페이지 수명만큼 입력값이 살아남아
 * 다음 모달에 이전 입력값이 그대로 노출되므로(prefill 버그) 그 방향 참조는 하지 않는다.
 * (여기서의 form -> flow 참조는 flow 가 이미 페이지 수명이라 폼 수명에 영향이 없다.)
 *
 * 채널 에러의 필드 반영은 현재 actor 의 error emit 을 직접 구독해서 한다. MfaForm 은 머신이
 * VERIFYING 에 진입할 때 마운트되고, 채널 에러 emit 은 요청 네트워크 왕복 뒤에 오므로(auto-send
 * 실패 포함) 구독이 항상 에러보다 먼저 걸린다. 세션 에러 확인은 flow 가 별도로 담당한다.
 */
export const mfaVerifyFormBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const { mfaVerifyActorAtom } = bunja.use(mfaVerifyFlowBunja);
  const form = {
    email: bunja.use(emailOtpBunja),
    sms: bunja.use(smsOtpBunja),
    totp: bunja.use(totpOtpBunja),
  };

  const setError = (channel: MfaVerifyChannel, error: string) => {
    form[channel].setError(error);
  };

  const resetField = (channel: MfaVerifyChannel) => {
    form[channel].clear();
  };

  const resetAllFields = () => {
    form.email.clear();
    form.sms.clear();
    form.totp.clear();
  };

  const subscribeChannelError = () =>
    store.get(mfaVerifyActorAtom).on('error', ({ channel, error }) => {
      if (!getErrorCode(error)) resetField(channel);
      else setError(channel, getErrorMessage(error));
    });

  bunja.effect(() => {
    let errorSubscription = subscribeChannelError();
    // 재구독을 unsubscribe→subscribe 한 쌍으로 묶어 순서 실수/누락을 막는다.
    const resubscribeChannelError = () => {
      errorSubscription.unsubscribe();
      errorSubscription = subscribeChannelError();
    };

    // 새 verify flow(=새 actor)마다 입력값 초기화 + 새 actor 로 에러 구독 재연결.
    // 정상 재오픈은 dispose→새 인스턴스라 리셋이 저절로 되지만, OTP 입력 atom 이 전역 스코프라
    // 모달을 닫았다 여는 사이 bunja 인스턴스가 (오버레이 전환/애니메이션 타이밍상) 재사용되면
    // unmount 기반 리셋이 안 걸린다. 그 재사용 인스턴스가 새 flow 를 만났을 때를 여기서 잡는다.
    const unsubscribeActor = store.sub(mfaVerifyActorAtom, () => {
      resetAllFields();
      resubscribeChannelError();
    });

    return () => {
      errorSubscription.unsubscribe();
      unsubscribeActor();
    };
  });

  return {
    setError,
    resetField,
  };
});

mfaVerifyFormBunja.debugLabel = 'mfaVerifyFormBunja';

interface Options {
  email?: string;
  phoneNumber?: string;
  passwordResetToken?: string;
  authToken?: string;
  withdrawalRequestId?: string;
  silent?: boolean; // Modal을 띄우지 않고 진행할 때 사용
  /**
   * Machine state가 DONE으로 final 상태일 때
   * @param mfaSessionToken
   */
  onDone?: (mfaSessionToken: string) => unknown;
  /**
   * Verify flow가 완료되어 Confirm 버튼을 눌렀을 때
   * @param mfaSessionToken
   */
  onConfirm?: (mfaSessionToken: string) => unknown;
}

export const mfaVerifyFlowBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const overlay = bunja.use(overlayBunja);
  const { checkSessionError } = bunja.use(authBunja);

  const mfaVerifyActorAtom = atom<Actor<typeof mfaVerifyMachine>>(createActor(mfaVerifyMachine));
  const subscriptionAtom = atom<Subscription>({} as Subscription);
  const eventSubscriptionAtom = atom<Subscription>({} as Subscription);

  // flow 는 세션 에러 확인만 한다. 채널 에러의 필드 반영은 MfaForm(모달)이 actor 를 직접 구독해 처리한다.
  const handleChannelError = ({ error }: { error: unknown }) => {
    checkSessionError(error);
  };

  function createMfaVerifyActor() {
    const actor = createActor(mfaVerifyMachine);
    store.set(mfaVerifyActorAtom, actor);
    return store.get(mfaVerifyActorAtom);
  }

  function destroy() {
    overlay.close(MfaVerifyFlowModalId);
    store.get(eventSubscriptionAtom)?.unsubscribe?.();
    store.get(subscriptionAtom)?.unsubscribe?.();
    store.get(mfaVerifyActorAtom)?.stop();
  }

  function startVerify(mfaScope: MfaScope, options: Options) {
    const mfaVerifyActor = createMfaVerifyActor();

    if (!mfaVerifyActor) return;

    const passkeyAvailable = !passkeyUnavailableScope.includes(mfaScope);
    store.set(eventSubscriptionAtom, mfaVerifyActor.on('error', handleChannelError));
    mfaVerifyActor.start().send({
      type: 'initiate',
      mfaScope,
      passkeyAvailable,
      authToken: options?.authToken,
      email: options?.email,
      maskedPhoneNumber: options?.phoneNumber,
      passwordResetToken: options?.passwordResetToken,
      withdrawalRequestId: options?.withdrawalRequestId,
    });

    if (!options.silent)
      overlay.open(
        ({ isOpen, onClose, unmount }) => (
          <MfaVerifyFlowModal
            isOpen={isOpen}
            onClose={onClose}
            onConfirm={async (mfaSessionToken) => {
              try {
                await options?.onConfirm?.(mfaSessionToken);
              } finally {
                destroy();
              }
            }}
            onExit={unmount}
          />
        ),
        MfaVerifyFlowModalId,
      );

    store.set(
      subscriptionAtom,
      mfaVerifyActor.subscribe({
        complete: () => {
          const { output, value } = mfaVerifyActor.getSnapshot();
          if (value === 'NO_REQUIRED' && output) options?.onConfirm?.(output.mfaSessionToken);
          else if (output) options?.onDone?.(output.mfaSessionToken);
        },
        error: (error) => {
          snackbar.error({ message: getErrorMessage(error) });
          overlay.close(MfaVerifyFlowModalId);
        },
      }),
    );
  }

  bunja.effect(() => {
    return () => {
      destroy();
    };
  });

  return {
    mfaVerifyActorAtom,
    startVerify,
    destroy,
  };
});

mfaVerifyFlowBunja.debugLabel = 'mfaVerifyFlowBunja';
